import { useMemo } from 'react';
import { SWRConfiguration } from 'swr';

import { useLoggedInUserRoles } from '~/components/Auth';
import { useArraySWR } from '~/hooks/useSWR';
import { fetchFromOldApi } from '~/services/Http';
import { isRoleHR, isRoleHRAdmin, isRoleManager } from '~/services/Permissions';

export type RewardingProposalType =
  | 'ALLOWANCE_PROPOSAL'
  | 'BENEFIT_PROPOSAL'
  | 'SALARY_PROPOSAL';

export type WorkflowConfig = {
  fWorkflowName: RewardingProposalType;
  fWorkflowEmpCapability: string;
  fWorkflowMgrCapability: string;
  fWorkflowHigherMgrCapability: string;
  fWorkflowHrCapability: string;
};

export const getWorkflowSettings = (): Promise<WorkflowConfig[]> => {
  return fetchFromOldApi('/json/company/config/workflow').then((r) => r.json());
};

export const useWorkflowConfig = (swrConfig?: SWRConfiguration) => {
  const loggedInUserRoles = useLoggedInUserRoles();
  const isHRAdmin = isRoleHRAdmin(loggedInUserRoles);
  const isHR = isRoleHR(loggedInUserRoles);
  const isManager = isRoleManager(loggedInUserRoles);

  const { data: workflowSettings, isLoading } = useArraySWR<WorkflowConfig>(
    'workflowConfig',
    () => getWorkflowSettings(),
    swrConfig,
  );

  // Memoize so the returned reference is stable across renders. Consumers
  // (RehireEmployee, OverlappingEmployment) put this in useEffect dependency
  // arrays that recreate the Formio form — an unstable reference rebuilds the
  // form on every parent re-render and wipes any data the user had entered.
  const workflowConfig = useMemo(
    () =>
      workflowSettings?.length
        ? workflowSettings.reduce((acc, wfl) => {
            const canAdd =
              (+wfl.fWorkflowMgrCapability[0] >= 1 && isManager) ||
              (+wfl.fWorkflowHrCapability[0] >= 1 && isHR) ||
              isHRAdmin;
            return {
              ...acc,
              [wfl.fWorkflowName]: { ...wfl, renderProposal: canAdd },
            };
          }, {} as Record<RewardingProposalType, boolean>)
        : null,
    [workflowSettings, isHRAdmin, isHR, isManager],
  );

  return { workflowConfig, isLoading };
};
